import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Loader2, Lock, ShieldAlert } from 'lucide-react';
import { API } from '../App';
import AppTopHeader from '../components/AppTopHeader';

const LoginPage = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState('phone'); // phone or otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => setOtpTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, {
        phone: phone.startsWith('+91') ? phone : `+91${phone}`
      });
      
      toast.success('OTP sent to your phone!');
      
      setOtpSent(true);
      setOtpTimer(300);
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        phone: phone.startsWith('+91') ? phone : `+91${phone}`,
        otp
      });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('Login successful!');
      if (window.fbq) window.fbq('track', 'Lead');
      setIsAuthenticated(true);
      const query = new URLSearchParams(location.search || '');
      const nextPath = query.get('next');
      const safeNextPath = nextPath && nextPath.startsWith('/') ? nextPath : '/dashboard';
      navigate(safeNextPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <AppTopHeader backToHome backLabel="Back to Home" />
      <div className="px-4 py-8 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
              {step === 'phone' ? 'Welcome Back' : 'Verify OTP'}
            </h1>
            <p className="text-slate-600">
              {step === 'phone' ? 'Enter your phone number to continue' : 'Enter the code we sent to your phone'}
            </p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
          {step === 'phone' ? (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-4 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-slate-600">
                    +91
                  </span>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    placeholder="9876543210"
                    maxLength="10"
                    className="flex-1 h-12 rounded-r-lg border border-slate-200 px-4 text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                    data-testid="phone-input"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center"
                data-testid="send-otp-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send OTP'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700 mb-1">OTP sent to</p>
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>+91 {phone}</span>
                  <button
                    type="button"
                    onClick={() => { setStep('phone'); setOtp(''); }}
                    className="text-sm text-[#0DB504] hover:underline font-medium"
                    style={{ fontFamily: 'Barlow' }}
                  >
                    Change
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Enter 6-Digit OTP
                  </div>
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  className="w-full px-4 py-3 text-2xl text-center font-mono rounded-lg border-2 border-gray-300 focus:border-[#0DB504] focus:outline-none transition"
                  data-testid="otp-input"
                  required
                />
              </div>

              <div className="text-center text-sm text-gray-600">
                {otpTimer > 0 ? (
                  <p>OTP expires in {Math.floor(otpTimer / 60)}:{String(otpTimer % 60).padStart(2, '0')}</p>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendOTP}
                    className="text-[#0DB504] hover:underline font-medium"
                    style={{ fontFamily: 'Barlow' }}
                  >
                    Resend OTP
                  </button>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-[#0DB504] hover:bg-[#0a9e03] disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
                style={{ fontFamily: 'Barlow' }}
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <ShieldAlert className="w-4 h-4" />
                    Verify OTP
                  </>
                )}
              </button>
            </form>
          )}

          {/* Privacy Note */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-900 text-center">
              <Shield className="w-4 h-4 inline mr-1" />
              Your phone number is encrypted and never shared publicly
            </p>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
